import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { Button, Spinner, Form } from 'react-bootstrap'
import Head from 'next/head'
import { AuthServices, UserServices } from 'services'
import Cookies from 'js-cookie'
import { loginSuccess, setAccessToken } from 'actions'
import { toast } from 'components/common/Toast'
import { LoadingOverlay } from 'components/common'
import { AppConfigServices } from 'services/index'
import config from 'config'
import { PublicLanguage, ForgotPassword, AppleSignIn, GoogleLogin } from 'components/common/index'
import nextI18NextConfig from '../../i18n'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { connect } from 'react-redux'
import Link from 'next/link'
import { YandexLogin } from 'components/common/index'
import { MailruLogin } from 'components/common/index'
import { Facebook } from 'assets/icons/components'
import HCaptcha from '@hcaptcha/react-hcaptcha';


let logo = '/images/logo.png'

const Login = (props) => {
  const { dispatch, accessToken, locale } = props
  const { t } = useTranslation('login')
  const [isLoadingProvider, setIsLoadingProvider] = useState(false)
  const [isLoadingForm, setIsLoadingForm] = useState(false)
  const [isPageLoad, setIsPageLoad] = useState(false)
  const [inputValues, setInputValues] = useState({
    username: '', password: '', token: ''
  })
  const [isShowCaptcha, setIsShowCaptcha] = useState(false)
  const forgotPasswordRef = useRef()
  const captchaRef = useRef(null)
  const router = useRouter()

  useEffect(() => {
    getCurrentUser()
    
    let queries = new URLSearchParams(location.search)
    if (queries.get('reset_password') && forgotPasswordRef) {
      forgotPasswordRef?.current?.handleOpenModal()
    }
  }, [])

  const getCurrentUser = async () => {
    if (Cookies.get('accessToken')) {
      setIsPageLoad(true)
      const { user, error } = await UserServices.getProfile()
      setIsPageLoad(false)

      if (user) {
        dispatch(loginSuccess(user, accessToken))
        if (user?.gender_verified) {
          router.push('/live')
        } else {
          router.push('/verify-gender')
        }
      } else {
        Cookies.remove('accessToken')
      }
    } else {
      setIsPageLoad(false)
    }
  }

  const handleOnChange = event => {
    const value = event.target.value
    const id = event.target.id
    setInputValues({ ...inputValues, [id]: value })
  }

  const handleSocialLogin = async (provider, accessToken) => {
    if (accessToken) {
      setIsLoadingForm(true)

      const { token, error, is_new } = await AuthServices.socialGoogle({ provider: provider, ...accessToken })

      if (token) {
        dispatch(setAccessToken(token))
        Cookies.set('accessToken', token, { expires: 7 })
        if (is_new && (provider === 'google' || provider === 'apple')) {
          Cookies.get('gclid') && window.gtag && gtag('event', 'conversion', { send_to: 'AW-17596970317/2sRbCNG24cocEM3q8cZB' })
        }
        router.push('/live')
      }

      setIsLoadingForm(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoadingForm(true)
    try {
      const { token, error } = await AuthServices.login(inputValues)

      if (error) throw error

      dispatch(setAccessToken(token))
      Cookies.set('accessToken', token, { expires: 7 })

      router.push('/live')
    } catch (e) {
      if (e?.errors?.token) {
        setIsShowCaptcha(true)
      }
      toast.error(e?.message)
    }
    
    if (captchaRef.current) {
      captchaRef.current.resetCaptcha()
    }
    setIsLoadingForm(false)
  }

  if (isPageLoad) {
    return <LoadingOverlay />
  }

  return (
    <div className='login-container'>
      <Head>
        <title>Connectmingle | Login</title>
        {/* SEO page */}
        <meta name='description' content={t('description')} />
        <meta property='og:url' content={ config.URL + router.route } />
        <meta property='og:title' content='Connectmingle | Login' />
        <meta property='og:image' content={ config.URL + '/images/logo.png' } />
        <meta property='og:description' content={t('description')} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Connectmingle" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content='Connectmingle | Login' />
        <meta name="twitter:description" content={t('description')} />
        <meta name="twitter:image" content={ config.URL + '/images/logo.png' } />
        <meta name="twitter:url" content={ config.URL + router.route } />
        <meta name="robots" content="noindex" />
      </Head>
      <div className='login-container__login-wrapper'>
        <div className='login-wrapper__logo'>
          <Link href='/'>
            <img src={logo} className='logo__image' alt='Logo Connectmingle' width={198} height={50}/>
          </Link>
          <div className="content__locales">
            <PublicLanguage t={t} locale={locale} path="/login"/>
          </div>
        </div>
        <Form className='login-wrapper__form' onSubmit={handleSubmit}>
          <Form.Group controlId='username'>
            <Form.Label>{t('form.input-1.label')}</Form.Label>
            <Form.Control
              onChange={handleOnChange}
              value={inputValues.username}
              type='text'
              autoComplete='current-text'
              placeholder={t('form.input-1.placeholder')} />
          </Form.Group>
          <Form.Group controlId='password'>
            <Form.Label>{t('form.input-2.label')}</Form.Label>
            <Form.Control
              onChange={handleOnChange}
              value={inputValues.password}
              type='password'
              placeholder={t('form.input-2.placeholder')}
              autoComplete='current-password' />
              <ForgotPassword ref={forgotPasswordRef} t={t}>
                {t('Forgot password')}
              </ForgotPassword>
          </Form.Group>
          {
            isShowCaptcha
            ?
              <Form.Group>
                <HCaptcha
                  sitekey={process.env.HCAPTCHA_SITE_KEY}
                  onVerify={(token,ekey) => setInputValues({...inputValues, token: token})}
                  ref={captchaRef}
                />
              </Form.Group>
            : <></>
          }
          <Button
            variant='primary'
            type='submit'
            disabled={isLoadingForm || isLoadingProvider || !inputValues.username || !inputValues.password}
            size='lg'
            className='form__button'>
            {isLoadingForm ? <><Spinner
              as='span'
              animation='border'
              size='sm'
              style={{ marginBottom: '2px' }}
              role='status'
            /> {t('form.submit')}</> : <>{t('form.submit')}</>}
          </Button>
          <div className='form__account-links'>
            <div className='account-links__sign'>
              {t('not-yet-registered')}{' '}
              <Link href='/register'>
                <a style={{ color: '#ec0043' }}>{t('sign-up')}</a>
              </Link>
            </div>

            
          </div>
        </Form>

        <div className='login-wrapper__break-wrapper'>
          <div className='break-wrapper__text'>
            {t('or')}
          </div>
          <div className='break-wrapper__break'></div>
        </div>
        <div className='login-wrapper__login-buttons'>
          {/* <FacebookLogin
            appId={config.FACEBOOK_CLIENT_ID}
            callback={(res) => handleSocialLogin('facebook', res)}
            render={renderProps => (
              <button className="login-button__button login-button__button--facebook btn btn-primary" onClick={renderProps.onClick}>
                <Facebook />  Facebook
              </button>
            )}
          /> */}
          <GoogleLogin onSuccess={(accessToken) => handleSocialLogin('google', accessToken)} />

          <AppleSignIn
            onSuccess={handleSocialLogin}
            style={{ marginLeft: '15px' }}
          />
          {/* <div id="appleid-signin"></div>
          <script type="text/javascript" src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"></script> */}

          
{/* 
          <YandexLogin clientID={config.YANDEX_CLIENT_ID} onSuccess={(res) => handleSocialLogin('yandex', res)}>
            <button className="login-button__button login-button__button--yandex btn btn-primary">
              Yandex
            </button>
          </YandexLogin>

          <MailruLogin clientID={config.MAILRU_CLIENT_ID} onSuccess={(res) => handleSocialLogin('mailru', res)}>
            <button className="login-button__button login-button__button--mailru btn btn-primary">
              {t('Mail')}
            </button>
          </MailruLogin> */}

          {/* <Button
            className='login-button__button login-button__button--facebook'
            disabled={isLoadingProvider || isLoadingForm}
            onClick={() => !isLoadingProvider && handleLogin('facebook')}>
            {isLoadingProvider ? <><Spinner
              as='span'
              animation='border'
              size='sm'
              style={{ marginBottom: '2px' }}
              role='status'
            />  {t('login-facebook')}</> : <><img src={facebook} className='button__icon' alt='Image Facebook' /> {t('login-facebook')}</>}
          </Button> */}
          {/* <Button
            className='login-button__button login-button__button--google'
            disabled={isLoadingProvider || isLoadingForm}
            onClick={() => !isLoadingProvider && handleLogin('google')}>
            {isLoadingProvider ? <><Spinner
              as='span'
              animation='border'
              size='sm'
              style={{ marginBottom: '2px' }}
              role='status'
            /> {t('login-google')}</> : <><img src={google} className='button__icon' alt='Image Google'/> {t('login-google')}</>}
          </Button> */}
        </div>
        <div className='login-wrapper__logo'>
          <Link style={{ color: '#ec0043' }} href='/trial'>
            <a style={{ color: '#ec0043' }} >← {t('back')}</a>
          </Link>
        </div>
      </div>
    </div>
  )
}

// Login.getInitialProps = async () => ({
//   namespacesRequired: ['common', 'login'],
// });

export const getStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale , ['common', 'login'], nextI18NextConfig)),
    locale: locale
  }
})

const mapStateToProps = (state) => ({
  accessToken: state.connectionReducer.accessToken,
})

export default connect(mapStateToProps)(Login)